import { useState } from 'react';
import { useAtomValue } from 'jotai';
import { useNavigate } from 'react-router';
import { Info, X } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { currentUserAtom } from '@/hooks/use-auth';
import { PILL_BUTTON } from '../ui-chrome';

/**
 * Composed from the vectors Figma exports for the illustration group (node 3445:10735) and served
 * from `public/`, not from Figma: the CDN URLs behind those exports expire after about a week, so
 * referencing them directly would leave a page that renders a broken image a few days after ship.
 */
const ILLUSTRATION_SRC = '/assets/social-agent-empty.svg';
const ILLUSTRATION_WIDTH = 395;
const ILLUSTRATION_HEIGHT = 176;

/** The Meta connect card lives on the integrations page; this agent only reads what it connects. */
const INTEGRATIONS_PATH = '/settings/integrations';

// Per account, because "I have read this" is a fact about a person on one brand's workspace, not
// about the browser. Two accounts in the same browser each get their own answer.
const DISMISS_KEY_PREFIX = 'social-agent-about-dismissed:';

const dismissKey = (accountId: number): string => `${DISMISS_KEY_PREFIX}${accountId}`;

/**
 * localStorage throws rather than returning an error in the cases that matter (Safari private
 * browsing, a full quota, storage blocked by policy), so both helpers absorb it. A banner that
 * cannot remember it was dismissed is a much smaller problem than a settings page that crashes,
 * but it is still worth knowing about, so neither path fails silently.
 */
const readDismissed = (accountId: number): boolean => {
	try {
		return localStorage.getItem(dismissKey(accountId)) === 'true';
	} catch (error) {
		console.warn('readDismissed: could not read the social agent banner state', { accountId, error });
		return false;
	}
};

const writeDismissed = (accountId: number): void => {
	try {
		localStorage.setItem(dismissKey(accountId), 'true');
	} catch (error) {
		console.warn('writeDismissed: could not persist the social agent banner dismissal', { accountId, error });
	}
};

/**
 * What the agent is for, shown above both empty states.
 *
 * It is dismissible and stays dismissed. The copy explains a concept rather than reporting
 * anything live, so past the first read it is pure furniture on a page whose whole job is to get
 * the first rule written.
 */
const AboutAgentBanner = () => {
	const currentUser = useAtomValue(currentUserAtom);
	const accountId = currentUser?.accountId;
	// Lazy initialiser: the stored answer is read once on mount rather than on every render. The
	// signed-in user is restored from localStorage synchronously at module load, so accountId is
	// already known here on the first paint.
	const [dismissed, setDismissed] = useState(() => (accountId === undefined ? false : readDismissed(accountId)));

	if (dismissed) return null;

	const handleDismiss = () => {
		setDismissed(true);
		if (accountId !== undefined) writeDismissed(accountId);
	};

	return (
		<div className='shrink-0 p-4'>
			{/*
			 * Figma 3767:16227: blue-100 on a gray-500 hairline, not blue-50 on blue-200 — the fill
			 * was a step too pale and the border a step too blue, so the banner read as a tint
			 * rather than as an information card. The title is blue-500, not blue-700.
			 *
			 * The icon is 20px, which the Alert's own `[&>svg]:size-4` would otherwise force to 16.
			 */}
			<Alert className='relative rounded-2xl border-gray-500 bg-blue-100 p-4 pr-12 text-blue-500 [&>svg]:size-5'>
				<Info aria-hidden='true' />
				<AlertTitle className='text-[14px] font-medium leading-[20px] text-blue-500'>
					What does this Agent Do ?
				</AlertTitle>
				<AlertDescription className='mt-1.5 text-[13px] leading-[18px] text-ink'>
					A Social Media Agent monitors your brand&apos;s social comments in real time and takes action on them
					(hiding, flagging for review, or auto-responding) so your brand stays protected around the clock without
					manual monitoring.
				</AlertDescription>
				<Button
					variant='ghost'
					size='icon-sm'
					className='absolute right-3 top-3 size-7 rounded-full text-blue-500 hover:bg-blue-200 hover:text-blue-500'
					onClick={handleDismiss}
					aria-label='Dismiss'
				>
					<X className='size-3.5' />
				</Button>
			</Alert>
		</div>
	);
};

interface SocialAgentEmptyStateProps {
	/**
	 * Whether any Meta channel is connected. Drives which call to action shows: without a channel
	 * a rule has nothing to run against, so connecting comes first.
	 */
	hasChannels: boolean;
	onCreateRule: () => void;
}

/**
 * The page with no rules on it, in its two flavours.
 *
 * The header above this (search, Create rule) is deliberately left alone in both. Figma gives the
 * disconnected state extra header controls that the connected state drops, which is backwards, so
 * the page keeps one header and only this block changes.
 */
export const SocialAgentEmptyState = ({ hasChannels, onCreateRule }: SocialAgentEmptyStateProps) => {
	const navigate = useNavigate();

	return (
		<div className='flex h-full flex-col'>
			<AboutAgentBanner />

			<div className='flex flex-1 flex-col items-center justify-center px-6'>
				{/* Decorative, and already described by the heading and copy underneath, so it is
				    hidden from screen readers rather than given a redundant alt. The negative margin
				    is the designed overlap between the artwork and "Get Started". */}
				<img
					src={ILLUSTRATION_SRC}
					alt=''
					aria-hidden='true'
					draggable={false}
					width={ILLUSTRATION_WIDTH}
					height={ILLUSTRATION_HEIGHT}
					className='-mb-8 h-[176px] w-[395px] max-w-full'
				/>

				<h2 className='text-[16px] font-semibold leading-[24px] tracking-[-0.3125px] text-ink'>Get Started</h2>

				<p className='mt-1.5 max-w-[356px] text-center text-[12px] leading-[16px] text-gray-900'>
					{hasChannels
						? 'Create a rule and start automating'
						: 'Connect your Meta account to create rules from your social channels, or create rules'}
				</p>

				<div className='mt-5 flex items-center gap-[9px]'>
					{!hasChannels && (
						<Button
							className={PILL_BUTTON}
							onClick={() => navigate(INTEGRATIONS_PATH)}
						>
							Connect meta
						</Button>
					)}
					{/*
					 * Figma offers "Create rules" in both empty states, but a rule authored with no
					 * connected channel can never fire and its channel picker would be empty. So it
					 * is disabled until Meta is connected, leaving "Connect meta" as the one live
					 * action — which is the order the copy already describes.
					 */}
					<Button
						variant='outline'
						className={PILL_BUTTON}
						disabled={!hasChannels}
						onClick={onCreateRule}
					>
						Create rules
					</Button>
				</div>
			</div>
		</div>
	);
};
